<template>
    <div v-if="visibleWarnings.length" class="m-2 overflow-hidden rounded-lg shadow">
        <div v-for="(warning, index) in displayedWarnings" :key="warning.id"
            class="flex min-h-8 flex-row items-center px-2 py-1"
            :class="[warning.colour, warning.textColour, { 'border-t border-black/10': index > 0 }]">
            <font-awesome-icon :icon="warning.symbol" class="shrink-0"></font-awesome-icon>
            <p class="min-w-0 flex-grow px-2">{{ warning.text }}</p>

            <button v-if="index === 0 && visibleWarnings.length > 1" type="button"
                class="mr-2 flex shrink-0 items-center gap-2 px-2 py-1 text-sm font-medium hover:underline"
                :aria-expanded="expanded" @click="expanded = !expanded">
                <span>{{ expanded ? "Collapse" : `Show all (${visibleWarnings.length})` }}</span>
                <font-awesome-icon :icon="expanded ? 'chevron-up' : 'chevron-down'"></font-awesome-icon>
            </button>

            <button type="button" class="shrink-0 m-0 p-2 opacity-70 hover:opacity-100"
                :aria-label="`Dismiss warning: ${warning.text}`" title="Dismiss from view"
                @click="dismissWarning(warning.id)">
                <font-awesome-icon icon="times"></font-awesome-icon>
            </button>
        </div>
    </div>
</template>
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { db } from '../../../db.js';

const route = useRoute();

const warningChecks = [
    {
        id: 'locked-for-release',
        check: designChange => designChange?.LockedForRelease === true,
        text: 'WARNING: DO NOT RELEASE. This Design Change has been locked for release.',
        colour: 'bg-orange',
        textColour: 'text-black',
        symbol: 'exclamation-circle'
    },
    {
        id: 'customer-access-enabled',
        check: designChange => designChange?.CustomerAccess?.Enabled === true,
        text: 'CUSTOMER ACCESS ENABLED: Customer members can currently view this Design Change.',
        colour: 'bg-blue-100',
        textColour: 'text-blue-900',
        symbol: 'eye'
    }
];

const activeWarnings = ref([]);
const dismissedWarningIds = reactive(new Set());
const expanded = ref(false);

const visibleWarnings = computed(() => activeWarnings.value.filter(
    warning => !dismissedWarningIds.has(warning.id)
));

const displayedWarnings = computed(() => expanded.value
    ? visibleWarnings.value
    : visibleWarnings.value.slice(0, 1)
);

async function checkDC(id) {
    const designChange = await db.once(`Database/DesignChanges/${id}`);
    if (id !== route.params.id) return;

    const checkResults = await Promise.all(warningChecks.map(async warning => ({
        ...warning,
        active: await warning.check(designChange)
    })));

    activeWarnings.value = checkResults.filter(warning => warning.active);
}

function dismissWarning(id) {
    dismissedWarningIds.add(id);
    if (visibleWarnings.value.length <= 1) expanded.value = false;
}

watch(() => route.params.id, async id => {
    dismissedWarningIds.clear();
    expanded.value = false;
    await checkDC(id);
}, { immediate: true });

</script>
